(() => {
  if (customElements.get('fp-deferred-featured-collection') && customElements.get('fp-deferred-pp-collection') && customElements.get('fp-deferred-pdp-section')) return;

  const pending = [];
  let busy = false;
  let stopped = false;
  let suspendedUntil = 0;
  let nextAllowedAt = 0;
  let wakeTimer = null;

  const runNext = async () => {
    if (busy || stopped) return;
    if (Date.now() < nextAllowedAt) {
      if (!wakeTimer) wakeTimer = setTimeout(() => {
        wakeTimer = null;
        runNext();
      }, nextAllowedAt - Date.now());
      return;
    }
    const section = pending.shift();
    if (!section) return;
    if (!section.isConnected) return runNext();

    busy = true;
    try {
      await section.load();
    } catch (error) {
      section.showError();
      // A server or rate-limit failure should not trigger a cascade of requests.
      stopped = true;
      for (const queued of pending.splice(0)) queued.showError();
    } finally {
      busy = false;
      // A rapid scroll should not turn a serial queue into a burst of Liquid renders.
      nextAllowedAt = Date.now() + 300;
      runNext();
    }
  };

  class FPDeferredShelf extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.lifecycle = new AbortController();
      this.retryButton = this.querySelector('[data-fp-deferred-retry]');
      this.status = this.querySelector('[data-fp-deferred-status]');
      this.retryButton?.addEventListener('click', () => {
        if (Date.now() < suspendedUntil) return this.showError();
        stopped = false;
        this.enqueue();
      }, { signal: this.lifecycle.signal });

      if (!('IntersectionObserver' in window)) {
        this.enqueue();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.enqueue();
        }
      }, { rootMargin: '250px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.lifecycle?.abort();
      this.controller?.abort();
      const index = pending.indexOf(this);
      if (index !== -1) pending.splice(index, 1);
      this.queued = false;
      this.initialized = false;
    }

    enqueue() {
      if (this.queued || !this.isConnected) return;
      if (stopped) return this.showError();
      this.queued = true;
      if (this.retryButton) {
        this.retryButton.hidden = true;
        this.retryButton.style.display = 'none';
      }
      if (this.status) this.status.textContent = this.dataset.productUrl ? 'Carregando conteúdo…' : 'Carregando produtos…';
      pending.push(this);
      runNext();
    }

    showError() {
      this.queued = false;
      if (!this.isConnected) return;
      if (this.status) this.status.textContent = Date.now() < suspendedUntil
        ? 'Aguarde um momento antes de tentar novamente.'
        : this.dataset.productUrl ? 'Não foi possível carregar este conteúdo.' : 'Não foi possível carregar esta vitrine.';
      if (this.retryButton) {
        this.retryButton.hidden = false;
        this.retryButton.style.display = '';
      }
      const fallback = this.querySelector('[data-fp-deferred-fallback]');
      if (fallback) fallback.hidden = false;
    }

    async load() {
      const id = this.dataset.sectionId;
      if (!id) throw new Error('Section ID ausente');
      const url = new URL(location.href);
      url.searchParams.delete('sections');
      // Shopify may remove this parameter from the visible URL after opening a draft.
      const theme = window.Shopify?.theme;
      if (theme?.id && theme.role === 'unpublished') {
        url.searchParams.set('preview_theme_id', String(theme.id));
      }
      url.searchParams.set('section_id', id);
      const rendered = await this.renderSection(url, id);
      if (!rendered) return;
      const current = document.getElementById(`shopify-section-${id}`);
      if (!current || rendered.querySelector('fp-deferred-featured-collection, fp-deferred-pp-collection')) throw new Error('Section Rendering incompleto');
      const content = Array.from(rendered.children).find((child) => child.tagName === 'DIV');
      if (!content) throw new Error('Conteúdo da vitrine ausente');
      const placeholder = Array.from(current.children).find((child) => child.matches?.('fp-deferred-featured-collection, .container-pp-collection-slideshow'));
      if (!placeholder) throw new Error('Placeholder da vitrine ausente');
      const skeleton = document.createElement('div');
      skeleton.setAttribute('aria-hidden', 'true');
      skeleton.style.cssText = 'position:absolute;inset:0;z-index:1;pointer-events:none';
      skeleton.append(placeholder.cloneNode(true));
      skeleton.querySelectorAll('fp-deferred-featured-collection, fp-deferred-pp-collection').forEach((element) => {
        const inert = document.createElement('div');
        inert.className = element.className;
        inert.innerHTML = element.innerHTML;
        element.replaceWith(inert);
      });
      rendered.style.height = `${current.getBoundingClientRect().height}px`;
      rendered.style.overflow = 'hidden';
      rendered.style.position = 'relative';
      content.style.visibility = 'hidden';
      rendered.append(skeleton);
      current.replaceWith(rendered);
      rendered.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } }));
      setTimeout(() => {
        if (!rendered.isConnected) return;
        rendered.style.height = '';
        rendered.style.overflow = '';
        content.style.visibility = '';
        skeleton.remove();
      }, 1800);
    }

    async renderSection(url, id) {
      const lifecycle = this.lifecycle;
      const controller = this.controller = new AbortController();
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (response.status === 429) {
          const retry = response.headers.get('Retry-After')?.trim();
          const delay = retry && /^\d+$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
          suspendedUntil = Date.now() + (Number.isFinite(delay) && delay >= 0 ? delay : 60000);
        }
        if (!response.ok) throw new Error(`Section Rendering HTTP ${response.status}`);
        const html = await response.text();
        if (!this.isConnected || lifecycle.signal.aborted) return;
        const rendered = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
        const current = document.getElementById(`shopify-section-${id}`);
        if (!rendered || !current || html.includes('Liquid error') || (response.url && new URL(response.url).pathname !== url.pathname)) {
          throw new Error('Section Rendering incompleto');
        }
        return rendered;
      } catch (error) {
        // Removing/reordering a section is not a storefront failure.
        if (!lifecycle.signal.aborted && (timedOut || !controller.signal.aborted)) throw error;
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.controller = null;
      }
    }
  }

  class FPDeferredPdpSection extends FPDeferredShelf {
    showError() {
      super.showError();
      // An empty reserved height must not hide the recovery button above the viewport.
      this.style.minHeight = '64px';
    }
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener('fp:connected-product-settled', () => {
        this.ready = true;
        if (this.requested) this.enqueue();
      }, { signal: this.lifecycle.signal });
      document.addEventListener('fp:connected-product-loading', () => {
        this.ready = false;
        this.controller?.abort();
        const index = pending.indexOf(this);
        if (index !== -1) pending.splice(index, 1);
        this.queued = false;
      }, { signal: this.lifecycle.signal });
    }
    enqueue() {
      this.requested = true;
      if (!this.ready) return;
      super.enqueue();
    }
    async load() {
      const id = this.dataset.sectionId;
      const productId = this.dataset.productId;
      const url = new URL(this.dataset.productUrl, location.href);
      if (url.origin !== location.origin || !url.pathname.startsWith(`${window.Shopify?.routes?.root || '/'}products/`) || !id || !productId) throw new Error('Destino da seção inválido');
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', id);
      const rendered = await this.renderSection(url, id);
      if (!rendered || !this.isConnected || !this.ready) return;
      if (document.querySelector('#MainContent .section-main-product')?.dataset.fpProductId !== productId) return;
      const current = this.closest('.shopify-section');
      if (!current || current.id !== rendered.id) throw new Error('Seção de destino inválida');
      current.replaceWith(rendered);
      rendered.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } }));
    }
  }

  if (!customElements.get('fp-deferred-featured-collection')) {
    customElements.define('fp-deferred-featured-collection', class extends FPDeferredShelf {});
  }
  if (!customElements.get('fp-deferred-pp-collection')) {
    customElements.define('fp-deferred-pp-collection', class extends FPDeferredShelf {});
  }
  if (!customElements.get('fp-deferred-pdp-section')) customElements.define('fp-deferred-pdp-section', FPDeferredPdpSection);
  window.FPDeferredPdpSections = {
    replace(context, url, criticalIds) {
      stopped = Date.now() < suspendedUntil;
      const root = document.getElementById('MainContent');
      const anchors = new Set([...root.querySelectorAll('a[href^="#"]')].map(link => link.getAttribute('href').slice(1)));
      // Read every height before replacing content; alternating reads/writes forces layout per section.
      const sections = [...root.children].filter(node => node.classList.contains('shopify-section')).flatMap(section => {
        const id = section.id.replace(/^shopify-section-/, '');
        if (criticalIds.includes(id) || section.querySelector('[data-fp-anchor-nav-section]')) return [];
        const height = Math.max(64, section.getBoundingClientRect().height);
        const ids = [...section.querySelectorAll('[id]')].map(node => node.id).filter(id => anchors.has(id));
        return [{ section, id, height, ids }];
      });
      for (const { section, id, height, ids } of sections) {
        section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId: id } }));
        const placeholder = document.createElement('fp-deferred-pdp-section');
        placeholder.dataset.sectionId = id;
        placeholder.dataset.productId = context.productId;
        placeholder.dataset.productUrl = url;
        placeholder.style.cssText = `min-height:${height}px;box-sizing:border-box`;
        for (const anchor of ids) {
          const span = document.createElement('span'); span.id = anchor; placeholder.append(span);
        }
        const status = document.createElement('p');
        status.setAttribute('data-fp-deferred-status', ''); status.setAttribute('role', 'status');
        status.textContent = 'Carregando conteúdo…';
        const retry = document.createElement('button');
        retry.type = 'button'; retry.className = 'button button--secondary';
        retry.setAttribute('data-fp-deferred-retry', ''); retry.hidden = true;
        retry.textContent = 'Tentar novamente';
        placeholder.append(status, retry);
        section.replaceChildren(placeholder);
      }
    },
  };
  document.addEventListener('click', event => {
    const href = event.target.closest?.('a[href^="#"]')?.getAttribute('href');
    if (!href || href === '#') return;
    let target;
    try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch (_) { return; }
    target?.closest('fp-deferred-pdp-section')?.enqueue();
  });
})();
